import { useEffect } from 'react';
import { useUserPreferences, usePrograms, useSettingsStore } from '@/store/useStore';
import { ReminderService } from '@/services/reminderService';
import { findUserProgram } from '@/utils/programMatching';

/**
 * Hook to manage class reminders using local browser notifications
 * No OneSignal or push notifications - only local notifications
 */
export function useClassReminders() {
  const userPreferences = useUserPreferences();
  const programs = usePrograms();
  const { notifications } = useSettingsStore();
  
  // Initialize and schedule reminders when dependencies change
  useEffect(() => {
    if (!userPreferences || !programs || programs.length === 0) {
      return;
    }

    const userProgram = findUserProgram(programs, userPreferences);

    if (!userProgram) {
      console.warn('User program not found for reminders');
      return;
    }

    // Initialize reminder service
    const reminderService = ReminderService.getInstance();
    reminderService.initialize();

    // Schedule reminders with current settings
    reminderService.scheduleReminders(userProgram, {
      classReminders: notifications.classReminders,
      reminderTime: notifications.reminderTime,
      pushNotifications: notifications.pushNotifications,
      soundEnabled: notifications.soundEnabled
    });

    // Cleanup on unmount
    return () => {
      reminderService.cleanup();
    };
  }, [userPreferences, programs, notifications]);

  // Check if reminders are enabled
  const areRemindersEnabled = () => {
    if (!('Notification' in window)) {
      return false;
    }
    return Notification.permission === 'granted' && 
           notifications.classReminders && 
           notifications.pushNotifications;
  };

  // Send test reminder
  const sendTestReminder = async (): Promise<boolean> => {
    const reminderService = ReminderService.getInstance();
    return reminderService.sendTestReminder('Database Systems', notifications.reminderTime);
  };

  return {
    areRemindersEnabled,
    sendTestReminder
  };
}
